import {getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem} from "@/lib/storageUtils";

const RETURN_TO_PARAM = "returnTo";
const RETURN_TO_STORAGE_KEY = "braiv.auth.returnTo";
const TRANSLATOR_INTENT = "translator";
const WORKSPACE_INVITE_INTENT = "workspace-invite";

export type SignupIntent = typeof TRANSLATOR_INTENT | typeof WORKSPACE_INVITE_INTENT;

export function getSafeReturnTo(value?: string | string[] | null): string | null {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) return null;

    let decoded = raw.trim();
    try {
        decoded = decodeURIComponent(decoded);
    } catch {
        // Keep the original value if it was not URI-encoded.
    }

    if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(decoded)) return null;
    if (decoded.startsWith("/login") || decoded.startsWith("/signup")) return null;
    return decoded;
}

export function getSafeReturnToFromLocation(): string | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return getSafeReturnTo(params.get(RETURN_TO_PARAM));
}

export function getSignupIntentFromLocation(): SignupIntent | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const intent = params.get("intent");
    if (intent === TRANSLATOR_INTENT || intent === WORKSPACE_INVITE_INTENT) return intent;
    return null;
}

export function isTranslationReturnTo(returnTo: string | null | undefined): returnTo is string {
    if (!returnTo) return false;
    return /^\/video\/[^/?#]+\/translate(?:[?#].*)?$/.test(returnTo);
}

export function isWorkspaceInviteReturnTo(returnTo: string | null | undefined): returnTo is string {
    if (!returnTo) return false;
    return /^\/workspace\/accept(?:[?#].*)?$/.test(returnTo);
}

export function buildAuthReturnTo(path: string): string {
    const safePath = getSafeReturnTo(path) || "/";
    return `${RETURN_TO_PARAM}=${encodeURIComponent(safePath)}`;
}

export function getAuthHref(route: "/login" | "/signup", path: string): string {
    return `${route}?${buildAuthReturnTo(path)}`;
}

export function getTranslatorSignupHref(path: string): string {
    return `${getAuthHref("/signup", path)}&intent=${TRANSLATOR_INTENT}`;
}

export function getWorkspaceInviteSignupHref(path: string): string {
    return `${getAuthHref("/signup", path)}&intent=${WORKSPACE_INVITE_INTENT}`;
}

export function persistReturnTo(returnTo: string | null): void {
    if (!returnTo) return;
    setSessionStorageItem(RETURN_TO_STORAGE_KEY, returnTo);
}

export function consumePersistedReturnTo(): string | null {
    const value = getSafeReturnTo(getSessionStorageItem(RETURN_TO_STORAGE_KEY));
    removeSessionStorageItem(RETURN_TO_STORAGE_KEY);
    return value;
}
